import Button from '../../../../Button/Button';
import Loading from '../../../../Loading/Loading';
import Show from '../../../../Show/Show';
import { COLORS } from '../../../../../config/designSystem/Colors';
import { Chip } from '../../../../presenters/Chip/Chip';
import { Feather } from '@expo/vector-icons';
import { IAnswerSelected } from './PresentSections';
import { ParagraphSBold } from '../../../../../config/designSystem/typography';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import { SPACING } from '../../../../../config/designSystem/Spacing';
import { mainTypoBold } from '../../../../../config/designSystem/typography';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useStores } from '../../../../../hooks/useStores';

const getMaxItems = (width: number) => {
  if (width > 1024) {
    return 50;
  } else if (width > 768) {
    return 14;
  } else if (width > 640) {
    return 8;
  } else {
    return 6;
  }
};

interface SectionProps {
  section: IAnswerSelected;
  options: IAnswerSelected[];
  rowLength?: number;
  onPress: (item: IAnswerSelected) => void;
}
const Section = ({ section, options, onPress }: SectionProps) => {
  const { language } = useStores();
  const labels = language.translate.MOBILE;
  const locale = language.language;
  const { width } = useWindowDimensions();
  const [expanded, setExpanded] = useState(options.some(o => o.selected));
  const [showAll, setShowAll] = useState(false);
  const hasInnerSelection = options.some(o => o.selected);
  useEffect(() => {
    if (hasInnerSelection) {
      setExpanded(true);
    }
  }, [hasInnerSelection]);
  const onPressSection = () => {
    setExpanded(ex => !ex);
    if (options.length === 0) {
      onPress(section);
    }
  };
  const itemsToShow = showAll || hasInnerSelection ? options : options.slice(0, getMaxItems(width));
  const date = section.wording ? new Date(section.wording) : undefined;
  const dateFormatted = date
    ? new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: '2-digit',
    }).format(date)
    : undefined;
  return (
    <View style={styles.section}>
      {/* In-house accordion (was react-native-paper's List.Accordion): a Pressable
          header row + conditional content. Same controlled `expanded` semantics. */}
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        style={[styles.sectionAccordion, expanded ? styles.sectionAccordionOpen : styles.sectionAccordionClosed]}
        onPress={onPressSection}
      >
        <ParagraphSBold style={styles.sectionAccordionTitle}>{dateFormatted}</ParagraphSBold>
        <Feather name={expanded ? 'chevron-up' : 'chevron-down'} size={22} color={COLORS.content.accent} />
      </Pressable>
      {expanded && (<>
        <View style={styles.sectionInner}>
          <Show when={options.length === 0}>
            <View style={{ minHeight: 150 }}>
              <Loading />
            </View>
          </Show>
          {itemsToShow.map(item => (
            <View key={item._id} style={styles.chipWrapper}>
              <Chip key={item._id} label={`${item.wording}`} onPress={() => onPress(item)} selected={item.selected} />
            </View>
          ))}
        </View>
        <Show when={!showAll && itemsToShow.length > 0 && itemsToShow.length < options.length}>
          <Button
            label={labels?.BUTTON?.SEE_MORE}
            onPress={() => setShowAll(true)}
            backgroundColor="transparent"
            color={COLORS.content.accent}
            paddingVertical={0}
            marginTop={8}
            marginBottom={8}
          />
        </Show>
      </>)}
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: COLORS.surface.contrast,
    borderRadius: 12,
    shadowColor: COLORS.surface['inverse-primary'],
    shadowOpacity: 0.1,
    shadowRadius: 4,
    shadowOffset: { height: -2, width: -2 },
  },
  sectionAccordion: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: COLORS.surface.contrast,
    height: SPACING['sp-48'],
    paddingHorizontal: SPACING['sp-16'],
    borderTopEndRadius: 12,
    borderTopStartRadius: 12,
  },
  sectionAccordionOpen: {
    borderTopEndRadius: 12,
    borderTopStartRadius: 12,
  },
  sectionAccordionClosed: {
    borderRadius: 12,
  },
  sectionAccordionTitle: {
    fontFamily: `${mainTypoBold}`,
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 18,
    color: COLORS.content.primary,
    margin: 0,
    padding: 0,
  },
  sectionInner: {
    paddingTop: SPACING['sp-8'],
    justifyContent: 'center',
    backgroundColor: COLORS.surface.contrast,
    paddingHorizontal: SPACING['sp-16'],
    paddingBottom: SPACING['sp-16'],
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    flex: 1,
    flexWrap: 'wrap',
    flexDirection: 'row',
    rowGap: SPACING['sp-24'],
  },
  chipWrapper: {
    paddingHorizontal: SPACING['sp-4'],
  },
});

export default observer(Section);
